import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  TablePagination,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const statData = [
  { title: "Total Candidates", value: 240 },
  { title: "Active Candidates", value: 185 },
  { title: "Hired Candidates", value: 200 },
  { title: "Rejected Candidates", value: 50 },
];

const candidateStats = [
  { name: "John Doe", applied: 5, interviews: 3, hired: true },
  { name: "Jane Smith", applied: 3, interviews: 2, hired: false },
  { name: "Alice Johnson", applied: 4, interviews: 4, hired: true },
  { name: "Bob Brown", applied: 6, interviews: 5, hired: true },
  { name: "Carol White", applied: 2, interviews: 1, hired: false },
  { name: "Dan Black", applied: 7, interviews: 6, hired: false },
  { name: "Eva Green", applied: 8, interviews: 7, hired: true },
];

const applicationsOverTime = [
  { month: "Jan", applications: 150 },
  { month: "Feb", applications: 180 },
  { month: "Mar", applications: 210 },
  { month: "Apr", applications: 230 },
  { month: "May", applications: 240 },
  { month: "Jun", applications: 250 },
  { month: "Jul", applications: 260 },
  { month: "Aug", applications: 255 },
  { month: "Sep", applications: 245 },
  { month: "Oct", applications: 235 },
  { month: "Nov", applications: 220 },
  { month: "Dec", applications: 200 },
];

const StatCard = ({ title, value }) => (
  <Card
    sx={{
      minHeight: 100,
      textAlign: "center",
      boxShadow: "none",
      border: "1px solid lightgrey",
      borderRadius: "20px",
    }}
  >
    <CardContent>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const CandidatePerformanceTable = ({ data, page, rowsPerPage }) => {
  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <TableContainer
      sx={{
        border: "1px solid transparent",
        borderRadius: "10px",
        maxWidth: "90vw",
        overflowX: "auto",
        margin: "auto",
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
            <StyledTableCell>Candidate Name</StyledTableCell>
            <StyledTableCell>Applications</StyledTableCell>
            <StyledTableCell>Interviews</StyledTableCell>
            <StyledTableCell>Hired</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((candidate, idx) => (
            <StyledTableRow key={idx}>
              <StyledTableCell>{page * rowsPerPage + idx + 1}</StyledTableCell>
              <StyledTableCell>{candidate.name}</StyledTableCell>
              <StyledTableCell>{candidate.applied}</StyledTableCell>
              <StyledTableCell>{candidate.interviews}</StyledTableCell>
              <StyledTableCell>{candidate.hired ? "Yes" : "No"}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ApplicationsChart = ({ data }) => (
  <Paper sx={{ mt: 4, p: 2 }}>
    <Typography variant="h6" mb={2}>
      Applications Over Time
    </Typography>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="applications" fill="#006CB8" />
      </BarChart>
    </ResponsiveContainer>
  </Paper>
);

const CandidateReportView = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
      >
        Candidate Reports
      </Typography>

      <Grid container spacing={2}>
        {statData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <StatCard title={item.title} value={item.value} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          mb={1}
        >
          Candidate Performance
        </Typography>

        <CandidatePerformanceTable
          data={candidateStats}
          page={page}
          rowsPerPage={rowsPerPage}
        />

        <TablePagination
          component="div"
          count={candidateStats.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 75, 100]}
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            "& .MuiTablePagination-selectLabel": {
              fontSize: { xs: "12px", sm: "14px" },
              fontFamily: "Geist, sans-serif",
            },
          }}
        />
      </Box>

      <ApplicationsChart data={applicationsOverTime} />
    </Box>
  );
};

export default CandidateReportView;
